import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { useState } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage } from 'react-intl';
import { useTimeoutFn } from 'react-use';
import styled, { keyframes } from 'styled-components';

import type { Position } from '__generated__/globalTypes';
import { ScoreStatus } from '__generated__/globalTypes';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { LabelS } from 'atoms/typography';
import { CardImg } from 'components/card/CardImg';
import { PositionIcon } from 'components/lineup/PositionIcon';
import { Score } from 'components/scoring/Score';
import { range } from 'lib/arrays';
import { positionShortNames } from 'lib/players';
import { thinMaterial } from 'style/utils';

import type { PvETeam_anyCard } from './__generated__/index.graphql';

type TeamItem = PvETeam_anyCard;

type PositionMapItem = {
  position: Position;
  label?: MessageDescriptor;
};

type TeamProp = {
  team: TeamItem[];
  displayResult: boolean;
  label: string;
  currentSelection: number;
  viewTransition?: boolean;
  positionsMap: PositionMapItem[];
};

const appearIn = keyframes`
  0% {
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  50% {
    scale: 1.1;
  }
  100% {
    scale: 1;
  }
`;

const SlotPlaceholder = styled(Vertical)`
  height: 100%;
  opacity: 0.1;
  word-break: break-word;
  padding: var(--half-unit);
  &.selected {
    opacity: 1;
    background: var(--c-nd-50);
  }
`;

const Slots = styled(Vertical)`
  border-radius: var(--unit);
  background: var(--c-nd-100);
  ${thinMaterial}
  gap: var(--half-unit);
  padding: 1.5vmin;
`;

const Slot = styled(Vertical).attrs({ center: true })`
  flex: 1;
  justify-content: flex-start;
`;

const CardImgWrapper = styled.div`
  width: 100%;
  aspect-ratio: var(--card-aspect-ratio);
  border-radius: var(--unit);
  border: 1px solid var(--c-nd-100);
  overflow: hidden;
  > *:first-child {
    animation: 0.3s ease ${appearIn} forwards;
  }
`;

const DelayedScoring = ({
  score: scoreProp,
  index,
}: {
  score: number;
  index: number;
}) => {
  const [score, setScore] = useState(0);
  useTimeoutFn(() => setScore(scoreProp), index * 600);
  return (
    <div style={{ opacity: score, transition: '.3s ease opacity' }}>
      <Score
        score={score}
        animationDelayMs={0}
        scoreStatus={ScoreStatus.FINAL}
        scoreType="PLAYER_SCORE"
      />
    </div>
  );
};

export const Team = ({
  team,
  displayResult,
  label,
  currentSelection,
  viewTransition = false,
  positionsMap,
}: TeamProp) => {
  return (
    <Slots>
      <LabelS color="var(--c-nd-700)" bold>
        {label}
      </LabelS>
      <Horizontal>
        {range(5).map(i => {
          const position = positionsMap[i];
          return (
            <Slot key={i} className="bottom">
              <CardImgWrapper>
                {team.at(i) ? (
                  <CardImg
                    src={team[i].pictureUrl}
                    style={{
                      viewTransitionName: viewTransition
                        ? `card-${team[i].slug}`
                        : undefined,
                    }}
                    width={320}
                    alt={team[i].anyPlayer.displayName}
                  />
                ) : (
                  <SlotPlaceholder
                    center
                    className={classNames({ selected: currentSelection === i })}
                  >
                    <PositionIcon position={positionsMap[i].position} />
                    <LabelS bold>
                      <FormattedMessage
                        {...(position.label
                          ? position.label
                          : positionShortNames[position.position])}
                      />
                    </LabelS>
                  </SlotPlaceholder>
                )}
              </CardImgWrapper>
              {team.at(i) ? (
                <>
                  {displayResult ? (
                    <DelayedScoring score={team[i].score || 0} index={i} />
                  ) : (
                    <Score score={0} scoreStatus={ScoreStatus.PENDING} />
                  )}
                </>
              ) : (
                <div style={{ opacity: 0 }}>
                  <Score score={0} scoreStatus={ScoreStatus.PENDING} />
                </div>
              )}
            </Slot>
          );
        })}
      </Horizontal>
    </Slots>
  );
};

Team.fragments = {
  team: gql`
    fragment PvETeam_anyCard on AnyCardInterface {
      slug
      pictureUrl
      score: averageScore(type: LAST_FIVE_SO5_AVERAGE_SCORE)
      anyPlayer {
        slug
        displayName
      }
    }
  ` as TypedDocumentNode<PvETeam_anyCard>,
};
